import { supabase } from "../supabase";
import type { UserRole, UserStatus, UserHoursSummary } from "@/types";
import {
  GROUP_SIGNUP_STATUS,
  PRIVATE_LESSON_ACTIVE_STATUSES,
} from "@/lib/constants/statuses";
/**
 * Users API helpers using Supabase
 */

export interface UserDashboard {
  hours: UserHoursSummary;
  active_pairs: number;
  upcoming_lessons: number;
  upcoming_lessons_approved: number;
  upcoming_lessons_pending: number;
  recent_transactions: number;
}

export const usersApi = {
  /**
   * Vytvoriť alebo aktualizovať používateľský profil
   * Používa sa napr. keď používateľ sa prihlási cez Google OAuth, ale ešte nemá záznam v users tabuľke
   */
  async upsertUser(data: {
    id: string;
    email: string;
    display_name?: string | null;
    avatar_url?: string | null;
    role?: UserRole;
    status?: UserStatus;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase.from("users").upsert(
        {
          id: data.id,
          email: data.email,
          display_name: data.display_name || null,
          avatar_url: data.avatar_url || null,
          role: data.role || "dancer",
          status: data.status || "pending",
          updated_at: new Date().toISOString(),
        },
        {
          onConflict: "id",
        },
      );

      if (error) throw error;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to upsert user",
      };
    }
  },

  /**
   * Synchronizovať avatar_url z Auth metadát do users tabuľky
   */
  async syncAvatarFromAuth(userId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const {
        data: { user: authUser },
        error: authError,
      } = await supabase.auth.getUser(userId);

      if (authError || !authUser) {
        throw new Error("User not found in Auth");
      }

      const avatarUrl =
        authUser.user_metadata?.avatar_url ||
        authUser.user_metadata?.picture ||
        null;

      const { error } = await supabase
        .from("users")
        .update({
          avatar_url: avatarUrl,
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to sync avatar from Auth",
      };
    }
  },

  /**
   * Fetch user hours from database
   * Aggregates hours from all user_hours entries for the user
   */
  async hours(
    userId: string,
  ): Promise<{ success: boolean; data?: UserHoursSummary; error?: string }> {
    try {
      const { data, error } = await supabase
        .from("user_hours")
        .select("*")
        .eq("user_id", userId);

      if (error) throw error;

      // Agregovať hodiny z všetkých záznamov
      let totalHours = 0;
      let usedHours = 0;
      let groupHours = 0;
      let privateHours = 0;

      if (data && Array.isArray(data)) {
        for (const record of data) {
          const remaining = record.hours_remaining || 0;
          const used = record.hours_used || 0;
          const total = remaining + used;

          totalHours += total;
          usedHours += used;

          // Rozdeliť podľa typu balíčka
          if (
            record.package_type === "group" ||
            record.package_type === "group_pair"
          ) {
            groupHours += remaining;
          } else if (
            record.package_type === "private_single" ||
            record.package_type === "private_pair"
          ) {
            privateHours += remaining;
          }
        }
      }

      const hours: UserHoursSummary = {
        total_hours: totalHours,
        used_hours: usedHours,
        remaining_hours: totalHours - usedHours,
        group_hours: groupHours,
        private_hours: privateHours,
      };

      return { success: true, data: hours };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to fetch hours",
      };
    }
  },

  /**
   * Fetch user dashboard data
   */
  async dashboard(
    userId: string,
  ): Promise<{ success: boolean; data?: UserDashboard; error?: string }> {
    try {
      // Fetch hours
      const hoursResult = await usersApi.hours(userId);
      if (!hoursResult.success || !hoursResult.data) {
        throw new Error("Failed to fetch hours");
      }

      // Fetch active pairs count
      const { count: pairsCount } = await supabase
        .from("pairs")
        .select("*", { count: "exact", head: true })
        .or(`user_a_id.eq.${userId},user_b_id.eq.${userId}`)
        .eq("status", "active");

      // Fetch upcoming lessons: group_signups (confirmed) + private_lessons (approved/pending, future)
      const futureFrom = new Date().toISOString();
      const [groupCountRes, approvedCountRes, pendingCountRes] =
        await Promise.all([
          supabase
            .from("group_signups")
            .select("*", { count: "exact", head: true })
            .eq("user_id", userId)
            .eq("status", "confirmed"),
          supabase
            .from("private_lessons")
            .select("*", { count: "exact", head: true })
            .eq("user_id", userId)
            .eq("status", "approved")
            .gte("start_at", futureFrom),
          supabase
            .from("private_lessons")
            .select("*", { count: "exact", head: true })
            .eq("user_id", userId)
            .eq("status", "pending")
            .gte("start_at", futureFrom),
        ]);
      const approvedCount = approvedCountRes.count ?? 0;
      const pendingCount = pendingCountRes.count ?? 0;
      const lessonsCount =
        (groupCountRes.count ?? 0) + approvedCount + pendingCount;

      // Fetch recent transactions count
      const { count: transactionsCount } = await supabase
        .from("transactions")
        .select("*", { count: "exact", head: true })
        .eq("user_id", userId)
        .gte(
          "created_at",
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        );

      const dashboard: UserDashboard = {
        hours: hoursResult.data,
        active_pairs: pairsCount || 0,
        upcoming_lessons: lessonsCount || 0,
        upcoming_lessons_approved: approvedCount,
        upcoming_lessons_pending: pendingCount,
        recent_transactions: transactionsCount || 0,
      };

      return { success: true, data: dashboard };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch dashboard",
      };
    }
  },

  /**
   * Získať budúce lekcie používateľa (skupinové + súkromné)
   */
  async upcomingLessons(userId: string): Promise<{
    success: boolean;
    data?: Array<{
      id: number;
      title: string;
      start_at: string;
      type: "group" | "private";
      location?: string;
    }>;
    error?: string;
  }> {
    try {
      const [groupSignupsResult, privateLessonsResult] = await Promise.all([
        supabase
          .from("group_signups")
          .select(
            `
            id,
            lesson_id,
            status,
            group_lessons (
              id,
              title,
              start_at,
              location
            )
          `,
          )
          .eq("user_id", userId)
          .in("status", [
            GROUP_SIGNUP_STATUS.CONFIRMED,
            GROUP_SIGNUP_STATUS.PENDING,
          ])
          .order("created_at", { ascending: false })
          .limit(5),
        supabase
          .from("private_lessons")
          .select("id, start_at, lesson_type, notes")
          .eq("user_id", userId)
          .in("status", PRIVATE_LESSON_ACTIVE_STATUSES)
          .gte("start_at", new Date().toISOString())
          .order("start_at", { ascending: true })
          .limit(5),
      ]);

      const lessons: Array<{
        id: number;
        title: string;
        start_at: string;
        type: "group" | "private";
        location?: string;
      }> = [];

      if (groupSignupsResult.data) {
        groupSignupsResult.data.forEach((signup: Record<string, unknown>) => {
          if (signup.group_lessons) {
            const gl = signup.group_lessons as Record<string, unknown>;
            lessons.push({
              id: gl.id as number,
              title: (gl.title as string) || "Skupinová lekcia",
              start_at: gl.start_at as string,
              type: "group",
              location: gl.location as string | undefined,
            });
          }
        });
      }

      if (privateLessonsResult.data) {
        privateLessonsResult.data.forEach((lesson: Record<string, unknown>) => {
          const title =
            lesson.lesson_type === "pair"
              ? "Súkromná lekcia (pár)"
              : "Súkromná lekcia";
          lessons.push({
            id: lesson.id as number,
            title,
            start_at: lesson.start_at as string,
            type: "private",
          });
        });
      }

      lessons.sort(
        (a, b) =>
          new Date(a.start_at).getTime() - new Date(b.start_at).getTime(),
      );

      return {
        success: true,
        data: lessons.slice(0, 5),
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch upcoming lessons",
      };
    }
  },

  /**
   * Získať stav onboarding používateľa
   */
  async getOnboardingStatus(userId: string): Promise<{
    success: boolean;
    data?: {
      onboarding_private_lessons_completed: boolean | null;
      onboarding_shown_at: string | null;
    };
    error?: string;
  }> {
    try {
      const { data, error } = await supabase
        .from("users")
        .select("onboarding_private_lessons_completed, onboarding_shown_at")
        .eq("id", userId)
        .single();

      if (error) throw error;

      return {
        success: true,
        data: data ?? undefined,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch onboarding status",
      };
    }
  },

  /**
   * Nastaviť onboarding_shown_at (pri prvom zobrazení modalu)
   */
  async setOnboardingShown(userId: string): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { error } = await supabase
        .from("users")
        .update({
          onboarding_shown_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("id", userId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to set onboarding shown",
      };
    }
  },

  /**
   * Označiť onboarding ako dokončený
   */
  async markOnboardingCompleted(
    userId: string,
    type: "private_lessons",
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (type === "private_lessons") {
        updateData.onboarding_private_lessons_completed = true;
        // Set onboarding_shown_at if not already set
        const { data: userData } = await supabase
          .from("users")
          .select("onboarding_shown_at")
          .eq("id", userId)
          .single();

        if (!userData?.onboarding_shown_at) {
          updateData.onboarding_shown_at = new Date().toISOString();
        }
      }

      const { error } = await supabase
        .from("users")
        .update(updateData)
        .eq("id", userId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to mark onboarding as completed",
      };
    }
  },
};
